import { useCallback, useRef } from 'react'
import { UseFormGetValues } from 'react-hook-form'

import { UploadFormService } from '@/services/uploadForm.service'

import { useAuth } from '@/auth/useAuth'
import { DEFAULT_DESCRIPTION } from '@/constants/languages'
import { convertTagsToBackendFormat } from '@/features/FormUploader/utils/tagsTransformers'
import {
	IFetchUploadForm,
	IUploaderForm,
	TPlatformValue
} from '@/types/uploadForm.interface'
import { toastError } from '@/utils/toastError'

import { isSourceRequired } from '../utils'

interface UseFormSubmitProps {
	taskId: string
	selectedPlatform: TPlatformValue
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	updatePlatformCompletion: (
		platform: TPlatformValue,
		formData: IUploaderForm
	) => void
	onSuccess?: () => void
	setIsUploaded?: (value: boolean) => void
	setIsFormUploading?: (value: boolean) => void
}

interface UploaderRoles {
	isInt: boolean
	isMain: boolean
}

const isEmptyString = (value?: string | null) => {
	return !value || value.trim() === ''
}

const normalizeTitleBrackets = (title?: string) => {
	return title?.replace(/[({]/g, '[').replace(/[)}]/g, ']')
}

const hasSelectedOption = (value: unknown) => {
	if (!value || Array.isArray(value) || typeof value !== 'object') return false

	const option = value as { value?: unknown; label?: unknown }
	return (
		!isEmptyString(String(option.value ?? '')) ||
		!isEmptyString(String(option.label ?? ''))
	)
}

const hasSelectedOptions = (value: unknown) => {
	return Array.isArray(value) && value.length > 0
}

const getPlatformsToValidate = (
	jsonData: IFetchUploadForm,
	selectedPlatform: TPlatformValue
) => {
	const orderedPlatforms = jsonData.platforms_order || []
	const existingPlatforms = Object.keys(
		jsonData.platforms || {}
	) as TPlatformValue[]

	return Array.from(
		new Set<TPlatformValue>([
			...orderedPlatforms,
			...existingPlatforms,
			selectedPlatform
		])
	)
}

const copyFirstPlatformFieldsToAdditionalPlatforms = (
	jsonData: IFetchUploadForm,
	selectedPlatform: TPlatformValue
) => {
	const platformsToValidate = getPlatformsToValidate(jsonData, selectedPlatform)
	const firstPlatform = jsonData.platforms_order?.[0] || platformsToValidate[0]
	const firstPlatformData = jsonData.platforms?.[firstPlatform]

	if (!firstPlatformData) return

	platformsToValidate.forEach(platform => {
		if (platform === firstPlatform) return

		const platformData = jsonData.platforms?.[platform]
		if (!platformData) return

		jsonData.platforms[platform] = {
			...platformData,
			categories: firstPlatformData.categories,
			region: firstPlatformData.region,
			country: firstPlatformData.country,
			rights: firstPlatformData.rights,
			UAERegulatedContent: firstPlatformData.UAERegulatedContent
		}
	})
}

const validatePlatformsForm = (
	jsonData: IFetchUploadForm,
	selectedPlatform: TPlatformValue,
	roles: UploaderRoles
) => {
	const platformsToValidate = getPlatformsToValidate(jsonData, selectedPlatform)
	const firstPlatform = jsonData.platforms_order?.[0] || platformsToValidate[0]

	return platformsToValidate.every(platform => {
		const platformData = jsonData.platforms?.[platform]
		if (!platformData) return false

		const hasCommonRequiredFields =
			!isEmptyString(platformData.title) &&
			!isEmptyString(platformData.location)

		if (!hasCommonRequiredFields) return false

		if (platform !== firstPlatform) return true

		const hasFirstPlatformRequiredFields =
			hasSelectedOptions(platformData.categories) &&
			hasSelectedOption(platformData.region) &&
			hasSelectedOption(platformData.country) &&
			!isEmptyString(platformData.rights) &&
			!isEmptyString(platformData.UAERegulatedContent)

		if (!hasFirstPlatformRequiredFields) return false

		if (isSourceRequired({ rights: platformData.rights, ...roles })) {
			return !isEmptyString(platformData.source)
		}

		return true
	})
}

export const useFormSubmit = ({
	taskId,
	selectedPlatform,
	uploaderFormGetValues,
	updatePlatformCompletion,
	onSuccess,
	setIsUploaded,
	setIsFormUploading
}: UseFormSubmitProps) => {
	const isSubmittingRef = useRef(false)

	const { isInt, isMain } = useAuth()

	return useCallback(async () => {
		if (isSubmittingRef.current) return
		isSubmittingRef.current = true

		try {
			setIsFormUploading?.(true)
			const currentFormData = uploaderFormGetValues()

			const formDataForCompletion = {
				...currentFormData
			}

			// Get all existing form data from server to merge with current data
			let allFormData: IFetchUploadForm | null = null
			try {
				allFormData = await UploadFormService.getFormData(taskId)
			} catch (error) {
				// If form doesn't exist yet, start with empty structure
				console.log('No existing form data, starting fresh')
			}

			// Convert tags from IOption[] to {name, id}[] format
			const tagsForBackend = convertTagsToBackendFormat(currentFormData.tags)

			// Start with existing data or create new structure
			const jsonData: IFetchUploadForm = allFormData
				? (JSON.parse(JSON.stringify(allFormData)) as IFetchUploadForm)
				: ({
						is_completed: false,
						platforms: {}
				  } as IFetchUploadForm)

			if (!jsonData.platforms) jsonData.platforms = {}

			// Refreshing current (selected) Platform first
			jsonData.platforms[selectedPlatform] = {
				...jsonData.platforms[selectedPlatform],
				title: currentFormData.title,
				description: currentFormData.description,
				location: currentFormData.location,
				categories: currentFormData.categories,
				tags: tagsForBackend,
				region: currentFormData.region,
				country: currentFormData.country,
				rights: currentFormData.rights,
				UAERegulatedContent: currentFormData.UAERegulatedContent,
				source: currentFormData.source,
				restrictions: currentFormData.restrictions
			} as unknown as Partial<IUploaderForm>

			// Then check all the platform and set description if it's empty
			Object.keys(jsonData.platforms).forEach(key => {
				const platform = key as TPlatformValue
				const platformData = jsonData.platforms[platform]

				if (!platformData) return

				jsonData.platforms[platform] = {
					...platformData,
					title: normalizeTitleBrackets(platformData.title)
				}

				if (isEmptyString(platformData.description)) {
					jsonData.platforms[platform] = {
						...jsonData.platforms[platform],
						description: DEFAULT_DESCRIPTION[platform]
					}
				}
			})

			copyFirstPlatformFieldsToAdditionalPlatforms(jsonData, selectedPlatform)

			const isFormValidForAllPlatforms = validatePlatformsForm(
				jsonData,
				selectedPlatform,
				{ isInt: Boolean(isInt), isMain: Boolean(isMain) }
			)

			if (!isFormValidForAllPlatforms) {
				toastError('Please fill in all required fields for all platforms!')
				return
			}

			await UploadFormService.sendFormData(taskId, jsonData)

			// Synchronization with CMS after completing upload form
			await new Promise<void>((resolve, reject) => {
				// Polling synchronization status
				const pollSync = async () => {
					try {
						const syncStatus = await UploadFormService.getSyncStatus(taskId)

						const loaded = syncStatus.loaded

						if (loaded === true) {
							await UploadFormService.sendFormData(taskId, {
								is_completed: true,
								platforms: jsonData.platforms
							})

							return resolve()
						}

						if (loaded === false) {
							return reject(new Error('Synchronization error'))
						}

						setTimeout(pollSync, 3000)
					} catch (err) {
						return reject(err)
					}
				}

				// Sending synchronization request to backend
				UploadFormService.syncFormData(taskId)
					.then(res => {
						if (res.error) {
							reject(new Error(res.error))
							return
						}
						pollSync()
					})
					.catch(reject)
			})

			setIsUploaded?.(true)

			// Update completion status after final submit
			updatePlatformCompletion(selectedPlatform, formDataForCompletion)

			onSuccess?.()
		} catch (error) {
			console.error('Error submitting form data:', error)
			setIsUploaded?.(false)

			toastError(`Error submitting form. Try again a bit later!`)
		} finally {
			setIsFormUploading?.(false)
			isSubmittingRef.current = false
		}
	}, [
		taskId,
		selectedPlatform,
		uploaderFormGetValues,
		updatePlatformCompletion,
		onSuccess,
		setIsUploaded,
		setIsFormUploading,
		isInt,
		isMain
	])
}
